import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

const LEAGUES = [
    { id: 'wooden', nameKey: 'league_wooden', icon: '/assets/league_wooden.png', min: 0, max: 500 },
    { id: 'bronze', nameKey: 'league_bronze', icon: '/assets/league_bronze.png', min: 500, max: 1500 },
    { id: 'silver', nameKey: 'league_silver', icon: '/assets/league_silver.png', min: 1500, max: 3000 },
    { id: 'gold', nameKey: 'league_gold', icon: '/assets/league_gold.png', min: 3000, max: null },
];

export const RatingScreen = ({ onBack, onStartGame }) => {
    const { t } = useLanguage();
    const [activeLeague, setActiveLeague] = useState('bronze');
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);

    // Initial load
    useEffect(() => {
        const init = async () => {
            try {
                const profile = await apiClient.getUserProfile();
                setUserProfile(profile);
                // Default to user's league or bronze
                setActiveLeague(profile.league || 'bronze');
            } catch (error) {
                console.error("Failed to load profile", error);
                setActiveLeague('bronze');
            }
        };
        init();
    }, []);

    // Preload icons
    useEffect(() => {
        LEAGUES.forEach(l => {
            const img = new Image();
            img.src = l.icon;
        });
    }, []);

    // Load leaderboard when league changes
    useEffect(() => {
        const loadLeaderboard = async () => {
            setLoading(true);
            try {
                const data = await apiClient.getLeaderboard(activeLeague);
                setLeaderboard(data.entries || []);
            } catch (error) {
                console.error("Failed to load leaderboard", error);
                setLeaderboard([]);
            } finally {
                setLoading(false);
            }
        };
        loadLeaderboard();
    }, [activeLeague]);

    return (
        <div className="flex flex-col h-full w-full px-4 pt-6 pb-6 gap-5 overflow-hidden relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between shrink-0 mb-1">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onBack}
                    className="w-10 h-10 p-0 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </Button>
                <h1 className="text-xl font-bold text-white tracking-widest uppercase text-glow">{t('rating_title')}</h1>
                <div className="w-10" /> {/* Spacer */}
            </div>

            {/* League Tabs - Grid Layout */}
            <div className="grid grid-cols-2 gap-2.5 shrink-0 mb-1">
                {LEAGUES.map(league => (
                    <button
                        key={league.id}
                        onClick={() => setActiveLeague(league.id)}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2.5 rounded-2xl text-[10px] font-bold uppercase transition-all border justify-center",
                            activeLeague === league.id
                                ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]"
                                : "bg-slate-900/60 border-slate-700/50 text-slate-500 grayscale opacity-70 hover:opacity-100 hover:grayscale-0"
                        )}
                    >
                        <img src={league.icon} alt={t(league.nameKey)} className="w-4 h-4 object-contain filter drop-shadow-md" />
                        <span className="mt-0.5 truncate">{t(league.nameKey)}</span>
                    </button>
                ))}
            </div>

            {/* Leaderboard List */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2.5 pr-1 custom-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-4">
                        <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                        <p className="text-xs text-slate-500 animate-pulse tracking-wide">{t('loading_data')}</p>
                    </div>
                ) : leaderboard.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-center p-4 opacity-50">
                        <p className="text-slate-400 text-sm font-medium whitespace-pre-wrap">{t('empty_league')}</p>
                    </div>
                ) : (
                    leaderboard.map((player) => {
                        const isMe = userProfile && player.telegram_id === userProfile.telegram_id;
                        return (
                            <div
                                key={player.telegram_id}
                                className={cn(
                                    "flex items-center gap-4 p-3.5 rounded-2xl border backdrop-blur-md transition-all relative overflow-hidden group",
                                    isMe
                                        ? "bg-cyan-500/10 border-cyan-500/40 shadow-[0_0_20px_rgba(6,182,212,0.15)]"
                                        : "bg-white/5 border-white/5 hover:bg-white/10"
                                )}
                            >
                                {isMe && <div className="absolute top-0 left-0 w-1 h-full bg-cyan-500 shadow-[0_0_10px_cyan]" />}

                                {/* Rank */}
                                <div className={cn(
                                    "w-8 text-center font-black text-xl italic",
                                    player.rank === 1 ? "text-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.6)]" :
                                        player.rank === 2 ? "text-slate-300 drop-shadow-[0_0_5px_rgba(203,213,225,0.4)]" :
                                            player.rank === 3 ? "text-amber-600 drop-shadow-[0_0_5px_rgba(217,119,6,0.4)]" :
                                                "text-slate-600"
                                )}>
                                    {player.rank}
                                </div>

                                {/* Avatar */}
                                <div className="relative shrink-0">
                                    <div className={cn(
                                        "w-11 h-11 rounded-full bg-slate-800 overflow-hidden border-2",
                                        isMe ? "border-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.3)]" :
                                            player.rank <= 3 ? "border-white/20" : "border-transparent"
                                    )}>
                                        {player.avatar_url ? (
                                            <img src={player.avatar_url} alt={player.nickname} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900 text-xs font-bold text-slate-400">
                                                {player.nickname.slice(0, 2).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    {player.rank <= 3 && (
                                        <div className="absolute -top-1.5 -right-1.5 text-base filter drop-shadow-md">
                                            {player.rank === 1 ? '👑' : player.rank === 2 ? '🥈' : '🥉'}
                                        </div>
                                    )}
                                </div>

                                {/* Name & Badge */}
                                <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                                    <div className={cn(
                                        "font-bold text-[15px] truncate leading-tight",
                                        isMe ? "text-cyan-400 text-glow" : "text-slate-200"
                                    )}>
                                        {player.nickname} {isMe && t('you')}
                                    </div>
                                    <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                                        <div className={cn("w-1.5 h-1.5 rounded-full", isMe ? "bg-cyan-500 animate-pulse" : "bg-slate-600")} />
                                        {t(`league_${(player.league || activeLeague).toLowerCase()}`) || player.league}
                                    </div>
                                </div>

                                {/* Score */}
                                <div className="font-mono font-bold text-white text-base bg-white/5 py-1 px-2 rounded-lg border border-white/5">
                                    {player.rating}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* User Stat Footer (Sticky) */}
            {userProfile && (
                <div className="shrink-0 pt-2 border-t border-white/10 animate-slide-up">
                    <div className="bg-gradient-to-r from-cyan-950/80 to-slate-900/80 border border-cyan-500/30 rounded-3xl p-1.5 pr-2 pl-2 flex items-center justify-between backdrop-blur-xl shadow-2xl">
                        <div className="flex items-center gap-3 bg-slate-950/50 rounded-2xl p-2 pr-4 border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center border border-cyan-500/30 p-1.5 relative overflow-hidden">
                                <div className="absolute inset-0 bg-cyan-500/20 blur-md" />
                                <img
                                    src={LEAGUES.find(l => l.id === (userProfile.league || 'bronze').toLowerCase())?.icon || LEAGUES[1].icon}
                                    alt="League"
                                    className="w-full h-full object-contain relative z-10"
                                />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[9px] text-cyan-400 uppercase font-bold tracking-wider mb-0.5">{t('your_rating')}</span>
                                <span className="text-lg font-black text-white leading-none">{userProfile.rating}</span>
                            </div>
                        </div>
                        <Button
                            size="md"
                            variant="primary"
                            className="px-6 h-12 rounded-2xl font-black tracking-widest text-sm shadow-[0_0_20px_rgba(6,182,212,0.3)] animate-pulse-slow"
                            onClick={onStartGame}
                        >
                            {t('to_battle')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
